import * as yup from "yup";
import { Button, FloatingLabel, Form, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useCallback, useState } from "react";
import { Formik } from "formik";
import PropTypes from "prop-types";
import { useMutation } from "react-query";

import RecaptchaField from "../../components/RecaptchaField/RecaptchaField";

import { getMagicLinkMutation } from "./queries/getMagicLink";
import { useNotifications } from "../../components/NotificationsContext";

const schema = yup.object().shape({
	email: yup.string().email("Must be a valid email address.").
		required("Please enter you account email address."),
	human: yup.string().matches("human", { message: "You must be human to do this." }).
		required("You must be human to do this.")
});

const MagicLinkButton = ({
	email,
	redirect
}) => {

	const { addNotification } = useNotifications();

	const [showMessage, setShowMessage] = useState(false);
	const [linkRequested, setLinkRequested] = useState(false);

	const initialValues = {
		email,
		human: ""
	};

	const {
		isLoading,
		mutateAsync: getLink
	} = getMagicLinkMutation(useMutation);

	const getMagicLink = useCallback(async ({ email: submittedEmail }) => {
		try {
			await getLink({
				email: submittedEmail,
				redirect
			});
			setLinkRequested(true);
		} catch (error) {
			addNotification({
				message: error.message || "Error getting magic link.",
				type: "danger"
			});
		}
	}, [email, redirect]);

	const toggleMessage = useCallback(() => {
		setShowMessage((show) => !show);
		setLinkRequested(false);
	}, []);

	return (
		<>
			<OverlayTrigger
				overlay={<Tooltip id="button-tooltip-2">Get an easy login link in your email inbox. No password needed.</Tooltip>}
			>
				<Button id="magic-link-button" onClick={toggleMessage} variant="success">
					{isLoading ? <Spinner animation="border" size="sm" /> : "Get Magic Link"}
				</Button>
			</OverlayTrigger>
			<Modal centered onHide={toggleMessage} show={showMessage}>
				{
					linkRequested
						? (
							<Modal.Body className="p-5 pb-4 text-center">
								Please check your email for the login link.<br />
								<Button onClick={toggleMessage} variant="link">Dismiss</Button>
							</Modal.Body>
						) : (
							<Modal.Body className="p-5 pb-4">
								<h3 className="text-neutral3">Get Magic Link</h3>
								<Formik
									initialValues={initialValues}
									onSubmit={getMagicLink}
									validationSchema={schema}
								>
									{({
										errors,
										handleBlur,
										handleChange,
										handleSubmit,
										touched,
										values
									}) => {
										const recaptchaCallback = useCallback((value) => {
											if (value) {
												handleChange({ target: { name: "human",
													value } });
											}
										}, []);
										return (
											<Form id="request-magic-link-form" onSubmit={handleSubmit}>
												<FloatingLabel className="mb-4" controlId="email" label="Email Address">
													<Form.Control
														// eslint-disable-next-line react/jsx-no-leaked-render
														isInvalid={touched.email && !!errors.email}
														// eslint-disable-next-line react/jsx-no-leaked-render
														isValid={touched.email && !errors.email}
														name="email"
														onBlur={handleBlur}
														onChange={handleChange}
														placeholder="Email Address"
														required
														type="email"
														value={values.email}
													/>
													{errors.email ? (
														<Form.Control.Feedback type="invalid">
															{errors.email}
														</Form.Control.Feedback>
													) : null}
												</FloatingLabel>

												{errors.human ? (
													<Form.Control.Feedback className="d-block" type="invalid">
														{errors.human}
													</Form.Control.Feedback>) : null}

												<RecaptchaField className="mb-4" onSuccess={recaptchaCallback} />

												<div className="d-flex justify-content-between">
													<Button
														onClick={toggleMessage}
														variant="light"
													>Cancel</Button>
													<Button
														disabled={isLoading}
														type="submit"
													>
														{isLoading ? <Spinner animation="border" size="sm" /> : "Get Link"}
													</Button>
												</div>
											</Form>
										);
									}}
								</Formik>
							</Modal.Body>
						)
				}
			</Modal>
		</>
	);
};

MagicLinkButton.propTypes = {
	email: PropTypes.string,
	redirect: PropTypes.string
};

MagicLinkButton.defaultProps = {
	email: "",
	redirect: "/"
};

export default MagicLinkButton;
